import { useState } from 'react';
import { useToast } from '../../hooks/useToast';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import api from '../../services/api';
import styles from './ImportPage.module.css';

const ImportPage = () => {
  const { toast } = useToast();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.endsWith('.csv')) {
      toast.error('Please select a CSV file');
      return;
    }

    setFile(selectedFile);
    setResults(null);

    // Preview first few rows
    const text = await selectedFile.text();
    const lines = text.split('\n').slice(0, 6);
    const headers = lines[0].split(',').map((h) => h.trim());
    const rows = lines.slice(1).map((line) => line.split(',').map((c) => c.trim()));
    setPreview({ headers, rows });
  };

  const handleImport = async () => {
    if (!file) return;

    setImporting(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api.post('/admin/members/import', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setResults(response);
      const created = response.results?.members?.created || 0;
      const updated = response.results?.members?.updated || 0;
      toast.success('Import complete: ' + created + ' created, ' + updated + ' updated');
    } catch (error) {
      toast.error(error.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreview(null);
    setResults(null);
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1>Import Clients</h1>
        <p>Bulk import clients using a CSV file</p>
      </div>

      {/* Instructions */}
      <Card className={styles.instructionsCard}>
        <h2>CSV Format Requirements</h2>
        <p>Your CSV file should include the following columns:</p>
        <div className={styles.columns}>
          <div className={styles.column}>
            <strong>Required:</strong>
            <ul>
              <li>email</li>
              <li>firstName</li>
              <li>lastName</li>
            </ul>
          </div>
          <div className={styles.column}>
            <strong>Optional (Client Info):</strong>
            <ul>
              <li>memberId</li>
              <li>phone</li>
              <li>dateOfBirth</li>
              <li>address</li>
              <li>city</li>
              <li>state</li>
              <li>zipCode</li>
            </ul>
          </div>
          <div className={styles.column}>
            <strong>Optional (Tier & Status):</strong>
            <ul>
              <li>plan_code</li>
              <li>agency_name</li>
              <li>membership_status</li>
            </ul>
            <p className={styles.smallText}>plan_code + agency_name maps to a custom membership tier</p>
            <p className={styles.smallText}>membership_status: "active" or "inactive"</p>
          </div>
          <div className={styles.column}>
            <strong>Optional (Dependents):</strong>
            <p className={styles.smallText}>Up to 5 dependents per member:</p>
            <ul>
              <li>dependent1_firstName</li>
              <li>dependent1_lastName</li>
              <li>dependent1_email</li>
              <li>dependent1_phone</li>
              <li>dependent1_dob</li>
              <li>dependent1_relationship</li>
            </ul>
            <p className={styles.smallText}>(Use dependent2_, dependent3_, etc. for more)</p>
          </div>
        </div>
        <div className={styles.notes}>
          <p><strong>Notes:</strong></p>
          <ul>
            <li>Dependents with email addresses will receive welcome emails with account setup links</li>
            <li>Valid relationships: spouse, child, other</li>
            <li>Date formats: YYYY-MM-DD or MM/DD/YYYY</li>
            <li>plan_code + agency_name are matched to a custom membership tier (e.g. "Oval Plus + Member" + "AOBG" → "Oval Plus + Member_AOBG")</li>
            <li>membership_status "active" enables the account; "inactive" deactivates it</li>
            <li>Rows sharing the same memberId are grouped as a family — oldest by DOB is the primary, others become dependents</li>
            <li>Existing members (by email) are updated, not skipped</li>
          </ul>
        </div>
        <a href="/templates/members-import.csv" className={styles.templateLink} download>
          Download Template CSV
        </a>
      </Card>

      {/* Upload Area */}
      {!results && (
        <Card className={styles.uploadCard}>
          <div className={styles.uploadArea}>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              className={styles.fileInput}
              id="csv-upload"
            />
            <label htmlFor="csv-upload" className={styles.uploadLabel}>
              <div className={styles.uploadIcon}>📁</div>
              <span className={styles.uploadText}>
                {file ? file.name : 'Click to select CSV file or drag and drop'}
              </span>
              {file && (
                <span className={styles.fileSize}>
                  {(file.size / 1024).toFixed(2)} KB
                </span>
              )}
            </label>
          </div>

          {/* Preview */}
          {preview && (
            <div className={styles.preview}>
              <h3>Preview (first 5 rows)</h3>
              <div className={styles.previewTable}>
                <table>
                  <thead>
                    <tr>
                      {preview.headers.map((header, i) => (
                        <th key={i}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row, i) => (
                      <tr key={i}>
                        {row.map((cell, j) => (
                          <td key={j}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {file && (
            <div className={styles.actions}>
              <Button variant="outline" onClick={handleReset}>
                Cancel
              </Button>
              <Button onClick={handleImport} loading={importing}>
                {importing ? 'Importing...' : 'Import Clients'}
              </Button>
            </div>
          )}
        </Card>
      )}

      {/* Results */}
      {results && (
        <Card className={styles.resultsCard}>
          <h2>Import Results</h2>
          <div className={styles.resultsStats}>
            <div className={styles.stat + ' ' + styles.success}>
              <span className={styles.statValue}>{results.results?.members?.created || 0}</span>
              <span className={styles.statLabel}>Clients Created</span>
            </div>
            <div className={styles.stat + ' ' + styles.success}>
              <span className={styles.statValue}>{results.results?.members?.updated || 0}</span>
              <span className={styles.statLabel}>Clients Updated</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.statValue}>{results.results?.members?.skipped || 0}</span>
              <span className={styles.statLabel}>Skipped</span>
            </div>
            <div className={styles.stat + ' ' + styles.error}>
              <span className={styles.statValue}>{results.results?.members?.failed || 0}</span>
              <span className={styles.statLabel}>Failed</span>
            </div>
          </div>

          {/* Dependent stats */}
          {(results.results?.dependents?.created > 0 || results.results?.dependents?.failed > 0) && (
            <div className={styles.dependentStats}>
              <h3>Dependents</h3>
              <div className={styles.resultsStats}>
                <div className={styles.stat + ' ' + styles.success}>
                  <span className={styles.statValue}>{results.results?.dependents?.created || 0}</span>
                  <span className={styles.statLabel}>Created</span>
                </div>
                <div className={styles.stat}>
                  <span className={styles.statValue}>{results.results?.dependents?.accountsCreated || 0}</span>
                  <span className={styles.statLabel}>Accounts + Emails Sent</span>
                </div>
                <div className={styles.stat + ' ' + styles.error}>
                  <span className={styles.statValue}>{results.results?.dependents?.failed || 0}</span>
                  <span className={styles.statLabel}>Failed</span>
                </div>
              </div>
            </div>
          )}

          {results.results?.details?.failed && results.results.details.failed.length > 0 && (
            <div className={styles.errorList}>
              <h3>Errors</h3>
              <ul>
                {results.results.details.failed.map((error, i) => (
                  <li key={i}>
                    {error.email}: {error.errors?.join(', ') || 'Unknown error'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className={styles.actions}>
            <Button onClick={handleReset}>Import Another File</Button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default ImportPage;
